/**
 * #716 — the check-in capture sheet. Kylie's ask from the 2026-07-29
 * walkthrough, verbatim: "So many people bring the same stuff every time…
 * it would save, and if it's different, that's when we'd write on the sheet
 * — but it would already be printed on there."
 *
 * ── THE ONE RULE THIS COMPONENT EXISTS TO HONOUR ────────────────────────
 * **It never blocks a check-in.** The dog is already checked in by the time
 * this mounts — `FrontDesk` fires the check-in POST first and opens the
 * sheet afterwards. So every exit from this sheet (Confirm, Skip, Esc,
 * overlay click, a dead network, closing the tab) leaves the floor in the
 * same correct state: dog in, snapshot optional. That ordering is the whole
 * safety argument, and it is why the header says "Checked in" in the past
 * tense before anything is fetched.
 *
 * ── TWO TAPS ────────────────────────────────────────────────────────────
 * Tap 1 is "Check In" on the row. Tap 2 is "Confirm" here. A repeat boarder
 * whose kit hasn't changed needs nothing else — the fields arrive pre-filled
 * from the dog's standing profile (copy-forward, resolved server-side) and
 * Confirm is the primary action. Editing is available but never required.
 *
 * ── WHAT "CARE" MEANS HERE (issue AC 1 + AC 4) ──────────────────────────
 * Kylie named three things, not one: "as far as belongings go and feeding
 * instructions and medications." So the sheet shows all three, each
 * pre-filled with what the dog already has on file:
 *
 *   · BELONGINGS — per-STAY (`booking_dogs.belongings_checked`) and, when
 *     "remember" is ticked, also the dog's standing kit. A one-off bag is
 *     still recorded against this stay with the toggle off.
 *   · FEEDING (method / food / notes) and MEDICATION NOTES — these are
 *     DOG-level columns with no per-stay equivalent, so they can only
 *     persist through `updateDefaults`. When the toggle is off they are
 *     disabled rather than editable-then-silently-dropped: accepting a
 *     keystroke you intend to throw away is the same lie as showing
 *     copied-forward data as confirmed.
 *   · "ANYTHING DIFFERENT THIS STAY" — the per-stay feeding override. Free
 *     text, never written back to the dog, always editable.
 *
 * AC 4 ("an edit at check-in → the NEXT stay pre-fills the edited values")
 * is therefore true for all of them: belongings and the feeding/meds
 * defaults ride the same PUT and the server writes the changed ones back
 * onto the Dog.
 *
 * ── DESIGN-PHILOSOPHY ───────────────────────────────────────────────────
 * · Counter Test — 6:45am, wet dogs at the door: the sheet opens with the
 *   answer already filled in and one big button to accept it. Skip is
 *   equally large and equally safe, and says so.
 * · P2 (the next action is obvious) — exactly one filled-primary button.
 * · P5 (never lie about data) — copied-forward values are labelled as
 *   copied-forward, never as confirmed. `captured: false` from the server
 *   drives a visible "from last stay" note; a first-time boarder gets the
 *   honest empty state, not a fabricated list.
 * · P7 (built for hands) — ≥44px targets, textareas sized for a thumb.
 * · Five states: loading (skeleton), empty (first-time boarder), default
 *   (pre-filled), error (what happened + Retry + "you can skip this"), and
 *   the copied-forward staleness state.
 *
 * ── DARK LAUNCH ─────────────────────────────────────────────────────────
 * The caller renders this ONLY when `config.features?.checkInCapture` is
 * on. No tenant enables it today, so check-in is byte-identical to before
 * this shipped until someone explicitly flips the flag.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchCareSnapshot,
  humanizeApiError,
  saveCareSnapshot,
  type CareSnapshot,
} from '../../lib/api.js';
import './CheckInCareSheet.css';

/**
 * Mirrors the server's `isBoardingName` in `admin-run-cards.ts` — the same
 * substring rule over the same field (`/today` sends `serviceType.name`,
 * the raw name, not the display name). Kept in sync by
 * `__tests__/CheckInCareSheet.test.tsx`, which pins the exact three
 * substrings; if the server's rule ever grows a fourth, that test is the
 * thing that fails.
 *
 * Why gate at all: belongings/feeding/meds capture is a BOARDING workflow.
 * A daycare drop-off is a one-tap flow and must stay one tap.
 */
export function isBoardingServiceName(name: string | null | undefined): boolean {
  if (!name) return false;
  const n = name.toLowerCase();
  return n.includes('board') || n.includes('overnight') || n.includes('lodging');
}

export interface CheckInCareSheetDog {
  readonly dogId: string;
  readonly name: string;
}

/**
 * The two structured Dog-level feeding columns. Values MUST match the
 * server's Zod enums in `admin-care-snapshot.ts` exactly — an unknown
 * string is a 400, and a 400 here would look to a staffer like the sheet
 * ate their edit. `''` is the UI's "not set" and is mapped to `null` on the
 * way out (the server's `.nullable()`), never sent as an empty string.
 */
const FEEDING_METHODS = [
  { value: 'free_feed', label: 'Free feed' },
  { value: 'scheduled', label: 'Scheduled meals' },
  { value: 'measured', label: 'Measured portions' },
] as const;

const FOOD_TYPES = [
  { value: 'dry', label: 'Dry' },
  { value: 'wet', label: 'Wet' },
  { value: 'raw', label: 'Raw' },
  { value: 'mixed', label: 'Mixed' },
] as const;

interface CheckInCareSheetProps {
  readonly apiUrl: string;
  readonly token: string;
  readonly bookingId: string;
  /** Every dog on this booking — one block each (a two-dog reservation
   *  genuinely brings two different bags). */
  readonly dogs: ReadonlyArray<CheckInCareSheetDog>;
  readonly onClose: () => void;
}

/** Per-dog editable state, seeded from the server's resolved snapshot. */
interface DogDraft {
  readonly dogId: string;
  readonly name: string;
  readonly belongings: string;
  readonly feedingOverride: string;
  /** Dog-level standing defaults — '' is "not set", never sent as ''. */
  readonly feedingMethod: string;
  readonly foodType: string;
  readonly feedingNotes: string;
  readonly medicationNotes: string;
  /** false ⇒ copied forward from the profile, nobody confirmed THIS stay. */
  readonly captured: boolean;
  /** True when the dog has no standing kit either — the first-visit path. */
  readonly firstVisit: boolean;
}

/** Which of a draft's fields an input can write. */
type DraftField =
  | 'belongings'
  | 'feedingOverride'
  | 'feedingMethod'
  | 'foodType'
  | 'feedingNotes'
  | 'medicationNotes';

type Phase = 'loading' | 'ready' | 'saving' | 'error';

function draftFrom(snap: CareSnapshot): DogDraft {
  const text = snap.belongings_text ?? '';
  // `?? {}` is deploy-order safety, the same reason the run card's fields
  // are optional: frontends auto-deploy on merge and the SERVER does not,
  // so a pre-#716 payload must render blanks, never crash the sheet that
  // just told a staffer their dog is checked in.
  const defaults = snap.defaults ?? {};
  return {
    dogId: snap.dog_id,
    name: snap.dog_name,
    belongings: text,
    feedingOverride: snap.feeding_override ?? '',
    feedingMethod: defaults.feeding_method ?? '',
    foodType: defaults.food_type ?? '',
    feedingNotes: defaults.feeding_notes ?? '',
    medicationNotes: defaults.medication_notes ?? '',
    captured: snap.captured,
    firstVisit: !snap.captured && text.trim().length === 0,
  };
}

export function CheckInCareSheet({
  apiUrl,
  token,
  bookingId,
  dogs,
  onClose,
}: CheckInCareSheetProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>('loading');
  const [drafts, setDrafts] = useState<ReadonlyArray<DogDraft>>([]);
  const [error, setError] = useState<string | null>(null);
  const [updateDefaults, setUpdateDefaults] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    dialogRef.current?.focus();
    return () => restoreFocusRef.current?.focus();
  }, []);

  // `dogs` is an array prop, so putting it in the effect's dep list would
  // refetch on every parent render that rebuilds the literal. Depend on the
  // IDENTITY of the set instead, and read the array through a ref.
  const dogsRef = useRef(dogs);
  dogsRef.current = dogs;
  const dogIdsKey = dogs.map((d) => d.dogId).join(',');

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);
    void (async () => {
      try {
        const results = await Promise.all(
          dogsRef.current.map((d) =>
            fetchCareSnapshot(apiUrl, token, bookingId, d.dogId),
          ),
        );
        if (cancelled) return;
        setDrafts(results.map((r) => draftFrom(r.care_snapshot)));
        setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        setError(
          humanizeApiError(err instanceof Error ? err.message : 'unknown'),
        );
        setPhase('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, token, bookingId, dogIdsKey, reloadKey]);

  // Focus the primary action ONCE, the moment the sheet becomes usable: the
  // two-tap path is open → Confirm, and a keyboard user should not have to
  // travel to it.
  //
  // Deliberately not on a timer and deliberately once-only. A deferred
  // focus() lands *after* a fast staffer has started typing and yanks the
  // caret out of the textarea mid-word — which is exactly how this was
  // written first, and what the "an edit is what gets written" test caught.
  const didFocus = useRef(false);
  useEffect(() => {
    if (phase !== 'ready' || didFocus.current) return;
    didFocus.current = true;
    primaryRef.current?.focus();
  }, [phase]);

  // Esc closes from ANYWHERE, and the DOCUMENT is the only owner of that.
  //
  // Two things forced this. (1) A React `onKeyDown` on the panel only sees
  // keys pressed inside the React tree — before the first click or tab,
  // focus is still on <body>, so Esc did nothing until you interacted with
  // the sheet first. (2) React synthetic `stopPropagation()` also stops the
  // NATIVE event, so a panel handler that "handles" Esc actively prevents a
  // document listener from ever seeing it. One owner, at the document.
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  /** Tab-trap only — Esc belongs to the document listener above. */
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusable = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  const setField = useCallback(
    (dogId: string, field: DraftField, value: string) => {
      setDrafts((prev) =>
        prev.map((d) => (d.dogId === dogId ? { ...d, [field]: value } : d)),
      );
    },
    [],
  );

  const confirm = useCallback(async () => {
    setPhase('saving');
    setError(null);
    try {
      for (const d of drafts) {
        // `belongings` is BOTH the per-stay snapshot and (when
        // updateDefaults is on) the dog's standing kit — the server derives
        // the second from the first, which is what makes "an edit at
        // check-in pre-fills the NEXT stay" true without a second field to
        // keep in sync. The server no-ops the Dog write when nothing
        // actually changed, so a plain confirm never churns `updated_at`.
        //
        // The feeding/meds defaults are sent ONLY when the toggle is on.
        // They have no per-stay column, so with it off there is nowhere for
        // them to land — and the inputs are disabled in that state, so what
        // is omitted here is exactly what the staffer could not have
        // edited. Sending them anyway would be harmless server-side (the
        // PUT skips the whole defaults block) but would make this payload
        // lie about what the sheet was allowed to change.
        const standingDefaults = updateDefaults
          ? {
              // '' → null: the server's enums are `.nullable()`, and ''
              // is not a member of either. Clearing is a real edit ("she's
              // off the raw diet now"), so null must reach the server.
              feedingMethod: d.feedingMethod === '' ? null : d.feedingMethod,
              foodType: d.foodType === '' ? null : d.foodType,
              // Free text: '' IS a valid clear, and the server normalizes
              // it to null in `changedDogDefaults`.
              feedingNotes: d.feedingNotes,
              medicationNotes: d.medicationNotes,
            }
          : {};
        await saveCareSnapshot(apiUrl, token, bookingId, d.dogId, {
          belongings: d.belongings,
          feedingOverride: d.feedingOverride,
          ...standingDefaults,
          updateDefaults,
        });
      }
      onClose();
    } catch (err) {
      // The check-in itself already succeeded — say so, so nobody re-taps
      // Check In on the row thinking it failed.
      setError(humanizeApiError(err instanceof Error ? err.message : 'unknown'));
      setPhase('error');
    }
  }, [apiUrl, token, bookingId, drafts, updateDefaults, onClose]);

  const dogLabel = dogs.map((d) => d.name).join(', ');
  const anyCopiedForward = drafts.some((d) => !d.captured && !d.firstVisit);
  /**
   * Feeding method / food / feeding notes / medications are DOG-level
   * columns with no per-stay equivalent, so with "remember" off there is
   * nowhere for an edit to go. Disabling them is the honest branch:
   * accepting keystrokes the save would drop is the same class of lie as
   * presenting copied-forward data as confirmed (Principle 5).
   */
  const defaultsDisabled = phase === 'saving' || !updateDefaults;

  return (
    <div
      className="ferro-checkin-sheet"
      data-testid="checkin-care-sheet"
    >
      <button
        type="button"
        aria-label="Close check-in details"
        data-testid="checkin-care-sheet-backdrop"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'default',
        }}
      />
      <div
        className="ferro-checkin-sheet__panel"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Check-in details for ${dogLabel}`}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={{ position: 'relative' }}
      >
        <header className="ferro-checkin-sheet__header">
          <div>
            {/* Past tense, before any fetch resolves: the dog IS in. */}
            <p className="ferro-checkin-sheet__eyebrow">Checked in</p>
            <h2 className="ferro-checkin-sheet__title">{dogLabel}</h2>
          </div>
          <p className="ferro-checkin-sheet__sub">
            Confirm what came in — or skip, nothing else is waiting on this.
          </p>
        </header>

        {phase === 'loading' ? (
          <div
            className="ferro-checkin-sheet__skeleton"
            data-testid="checkin-care-sheet-loading"
            aria-busy="true"
          >
            <span className="ferro-checkin-sheet__skeleton-line" />
            <span className="ferro-checkin-sheet__skeleton-line" />
            <span className="ferro-checkin-sheet__skeleton-line ferro-checkin-sheet__skeleton-line--short" />
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className="ferro-checkin-sheet__error" role="alert">
            <p className="ferro-checkin-sheet__error-what">{error}</p>
            {/* The recovery, and the reassurance. Both matter here: the
                worst outcome of this error is a staffer re-tapping Check In
                on a dog that is already in. */}
            <p className="ferro-checkin-sheet__error-how">
              {dogLabel} {dogs.length === 1 ? 'is' : 'are'} still checked in.
              You can retry, or skip this and note it on the run card.
            </p>
            <div className="ferro-checkin-sheet__actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost ferro-checkin-sheet__btn"
                onClick={() => setReloadKey((k) => k + 1)}
                data-testid="checkin-care-sheet-retry"
              >
                Retry
              </button>
              <button
                type="button"
                className="ferro-btn ferro-btn--primary ferro-checkin-sheet__btn"
                onClick={onClose}
                data-testid="checkin-care-sheet-error-dismiss"
              >
                Done
              </button>
            </div>
          </div>
        ) : null}

        {phase === 'ready' || phase === 'saving' ? (
          <>
            {anyCopiedForward ? (
              // Principle 5: copied-forward is USEFUL but UNCONFIRMED, and
              // the difference has to be visible. This is the whole reason
              // the server returns `captured` instead of just the values.
              <p
                className="ferro-checkin-sheet__carried"
                data-testid="checkin-care-sheet-carried"
              >
                Filled in from the last stay — check it still matches.
              </p>
            ) : null}

            <div className="ferro-checkin-sheet__dogs">
              {drafts.map((d) => (
                <section
                  key={d.dogId}
                  className="ferro-checkin-sheet__dog"
                  data-testid={`checkin-care-dog-${d.dogId}`}
                  data-first-visit={d.firstVisit ? 'true' : 'false'}
                >
                  {drafts.length > 1 ? (
                    <h3 className="ferro-checkin-sheet__dog-name">{d.name}</h3>
                  ) : null}

                  <label
                    className="ferro-checkin-sheet__label"
                    htmlFor={`belongings-${d.dogId}`}
                  >
                    Belongings
                  </label>
                  <textarea
                    id={`belongings-${d.dogId}`}
                    className="ferro-checkin-sheet__input"
                    rows={3}
                    value={d.belongings}
                    disabled={phase === 'saving'}
                    // Teaching copy, not fake data — the empty state says
                    // what to type, one item per line.
                    placeholder={'Pink leash\nBlue blanket\nSqueaky duck'}
                    onChange={(e) =>
                      setField(d.dogId, 'belongings', e.target.value)
                    }
                    data-testid={`checkin-care-belongings-${d.dogId}`}
                  />
                  {d.firstVisit ? (
                    <p
                      className="ferro-checkin-sheet__hint"
                      data-testid={`checkin-care-first-visit-${d.dogId}`}
                    >
                      First stay — one item per line, and it&apos;ll be waiting
                      here next time.
                    </p>
                  ) : null}

                  {/* ── Feeding + meds: the dog's STANDING instructions,
                      pre-filled for confirm-or-edit (AC 1). Editing them
                      here is what makes the NEXT stay pre-fill (AC 4) —
                      via the same PUT, gated by the toggle below. */}
                  <label
                    className="ferro-checkin-sheet__label"
                    htmlFor={`feeding-method-${d.dogId}`}
                  >
                    How they eat
                  </label>
                  <div className="ferro-checkin-sheet__row">
                    <select
                      id={`feeding-method-${d.dogId}`}
                      className="ferro-checkin-sheet__input ferro-checkin-sheet__select"
                      value={d.feedingMethod}
                      disabled={defaultsDisabled}
                      onChange={(e) =>
                        setField(d.dogId, 'feedingMethod', e.target.value)
                      }
                      aria-label={`Feeding method for ${d.name}`}
                      data-testid={`checkin-care-feeding-method-${d.dogId}`}
                    >
                      <option value="">Not set</option>
                      {FEEDING_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    <select
                      className="ferro-checkin-sheet__input ferro-checkin-sheet__select"
                      value={d.foodType}
                      disabled={defaultsDisabled}
                      onChange={(e) =>
                        setField(d.dogId, 'foodType', e.target.value)
                      }
                      aria-label={`Food type for ${d.name}`}
                      data-testid={`checkin-care-food-type-${d.dogId}`}
                    >
                      <option value="">Food — not set</option>
                      {FOOD_TYPES.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <label
                    className="ferro-checkin-sheet__label"
                    htmlFor={`feeding-notes-${d.dogId}`}
                  >
                    Feeding notes
                  </label>
                  <textarea
                    id={`feeding-notes-${d.dogId}`}
                    className="ferro-checkin-sheet__input"
                    rows={2}
                    value={d.feedingNotes}
                    disabled={defaultsDisabled}
                    placeholder="Half a cup, 7am and 5pm"
                    onChange={(e) =>
                      setField(d.dogId, 'feedingNotes', e.target.value)
                    }
                    data-testid={`checkin-care-feeding-notes-${d.dogId}`}
                  />

                  <label
                    className="ferro-checkin-sheet__label"
                    htmlFor={`medications-${d.dogId}`}
                  >
                    Medications
                  </label>
                  <textarea
                    id={`medications-${d.dogId}`}
                    className="ferro-checkin-sheet__input"
                    rows={2}
                    value={d.medicationNotes}
                    disabled={defaultsDisabled}
                    placeholder="Apoquel, one tablet with breakfast"
                    onChange={(e) =>
                      setField(d.dogId, 'medicationNotes', e.target.value)
                    }
                    data-testid={`checkin-care-medications-${d.dogId}`}
                  />

                  {/* Per-STAY, so it stays editable in both toggle states —
                      "obviously the locker would change" (Kylie). */}
                  <label
                    className="ferro-checkin-sheet__label"
                    htmlFor={`feeding-${d.dogId}`}
                  >
                    Anything different this stay
                  </label>
                  <textarea
                    id={`feeding-${d.dogId}`}
                    className="ferro-checkin-sheet__input"
                    rows={2}
                    value={d.feedingOverride}
                    disabled={phase === 'saving'}
                    placeholder="Just for this visit — half rations, owner brought her own food"
                    onChange={(e) =>
                      setField(d.dogId, 'feedingOverride', e.target.value)
                    }
                    data-testid={`checkin-care-feeding-${d.dogId}`}
                  />
                </section>
              ))}
            </div>

            <label className="ferro-checkin-sheet__remember">
              <input
                type="checkbox"
                checked={updateDefaults}
                disabled={phase === 'saving'}
                onChange={(e) => setUpdateDefaults(e.target.checked)}
                data-testid="checkin-care-update-defaults"
              />
              <span>
                Remember this for next time
                {/* Naming the opt-out case is what stops staff from
                    un-ticking it "to be safe" on every stay — and, now
                    that unticking also locks the feeding/meds fields,
                    saying so BEFORE they discover it. */}
                <span className="ferro-checkin-sheet__remember-hint">
                  {updateDefaults
                    ? 'Untick for a one-off bag that isn’t their usual.'
                    : 'One-off stay: belongings are still recorded, but feeding and meds keep the values on file.'}
                </span>
              </span>
            </label>

            <div className="ferro-checkin-sheet__actions">
              {/* Skip is deliberately as reachable as Confirm. Never
                  block a check-in on data entry — including socially. */}
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost ferro-checkin-sheet__btn"
                onClick={onClose}
                disabled={phase === 'saving'}
                data-testid="checkin-care-sheet-skip"
              >
                Skip
              </button>
              <button
                type="button"
                ref={primaryRef}
                className="ferro-btn ferro-btn--primary ferro-checkin-sheet__btn"
                onClick={() => void confirm()}
                disabled={phase === 'saving'}
                data-testid="checkin-care-sheet-confirm"
              >
                {phase === 'saving' ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
